// ABOUTME: The breathing scene: a sky strip that passes from storm to the storm row over a round, rolling sea bands and the swelling canoe.
// ABOUTME: Drawn in the Reset reference's 850 x 370 px stage, scaled as one piece and centered on its host (where the ring is), so every offset and ramp is the reference's (docs/plans/crossing/evidence/breath-reset-reference).

import type { CSSProperties, RefObject } from 'react';
import { Box } from '@mui/material';
import { keyframes, type Keyframes } from '@emotion/react';
import { WAVE_BACK_URL, WAVE_FRONT_URL } from '../../../theme/waveAssets';
import {
  CROSSING_BREATH_SKY_TOP, CROSSING_BREATH_SKY_MID, CROSSING_BREATH_SKY_BOTTOM,
  CROSSING_BREATH_STAGE_UPPER, CROSSING_BREATH_STAGE_LOWER, CROSSING_SEA_MID, CROSSING_SEA_DEEP,
  CROSSING_SUN_SCENE_GRADIENT_TOP, CROSSING_SUN_SCENE_GRADIENT_MID, CROSSING_SUN_SCENE_GRADIENT_BOTTOM,
  CROSSING_SUN_SCENE_SUN, CROSSING_SUN_SCENE_SUN_HALO, CROSSING_SUN_SCENE_SUN_GLOW,
  CROSSING_SUN_SCENE_CLOUD_FILL, CROSSING_SUN_SCENE_CLOUD_FILL_SOFT,
} from '../../../theme/crossingTokens';
import { RUNNER_PLATE_CLEARANCE_PX } from '../runner/plateClearance';
import { breathAmp, canoeRideHeight } from './breathMath';
import { useBreathStageScale } from './useBreathStageScale';
import SkyStrip from './SkyStrip';
import {
  CLOUD_SHAPES, SKY_ABOVE_STAGE, SKY_H, SKY_OVERSHOOT, SKY_PANEL_W, SKY_WINDOW_H, STAGE_H, STAGE_W, STORM_SKY_GRADIENT, motion as motionFor,
} from './stormSkyParts';
import WaaCanoe from './WaaCanoe';

// How far the backdrops (sky, sea) run past the stage so any screen is filled; the weather stays in the stage.
const REACH = 2500;
// How far above the host the scene's box reaches, so the sky fills behind the header and the strip.
const ABOVE_HOST_PX = 600;
// The wave bands are wider than any screen, so a wide viewport shows no gap at the water edge. The width is chosen so
// WAVE_BAND_W / 2 is 100 mod 200 and 0 mod 300, the same tile phase as the reference's 1800 px / -900 px bands.
const WAVE_BAND_W = 6600;

// The horizon and the deep-water line, as fractions of the stage.
const HORIZON_AT = '58%';
const DEEP_AT = '72%';
// Ready for compass: the scene zooms to the deck over 1.6 s and fades over 0.7 s from 0.8 s.
const ZOOM_SCALE = 4.2;
const ZOOM_TRANSITION = 'transform 1.6s cubic-bezier(.55,0,.8,.45), opacity 0.7s ease 0.8s';
const SWELL_SEC = 3;
const FRONT_BAND_LOOP_SEC = 5;
const FRONT_BAND_DELAY_SEC = -1.1;

// The canoe's bob: up and down once per cycle.
const swellCanoe = keyframes`
  0%, 100% { transform: translateY(-20px) rotate(1.4deg); }
  50% { transform: translateY(20px) rotate(-1.4deg); }
`;
const rollBack = keyframes`
  from { background-position-x: 150px; }
  to { background-position-x: -150px; }
`;
const rollFront = keyframes`
  from { background-position-x: 0px; }
  to { background-position-x: -200px; }
`;
// The new storm and the old sky roll left together as one track, two panels wide.
const pvRoll = keyframes`
  from { transform: translateX(0); }
  to { transform: translateX(-50%); }
`;
const ROLL_IN_SECONDS = 2.3;

// The sun scene's two white clouds, placed in the layer's content box.
const SUN_LAYER_CLOUDS = [
  { left: '60%', top: '46%', width: 180, height: 52, fill: CROSSING_SUN_SCENE_CLOUD_FILL },
  { left: '30%', top: '66%', width: 130, height: 38, fill: CROSSING_SUN_SCENE_CLOUD_FILL_SOFT },
];

// Opacities and angles go on the element's inline style, so a changing value never mints a new emotion class.
const round = (value: number, places: number) => +value.toFixed(places);

interface BreathSceneArtProps {
  x: number; // the sky strip's position 0..1: 0 shows the storm, 1 the storm row; 1 when no round is armed
  drift?: number; // seconds: in a round the strip passes by itself over this long (x is ignored); 0 holds it at x
  previousX?: number; // while a new storm rolls in (intro 'rolling'): the strip that rolls out is at this position
  armed: boolean; // a round is running or done: the sea scrolls and the canoe swells
  swellDur: number; // the ramp's current swell length in seconds; drives the wave amplitude, never the animation duration
  canoeRef?: RefObject<HTMLElement | null>; // the element the cue reads the swell's progress from
  backBandRef?: RefObject<HTMLElement | null>; // the bands carry the swell's playbackRate, set from outside
  frontBandRef?: RefObject<HTMLElement | null>;
  intro?: 'rest' | 'rolling' | 'round'; // the settled sky, a new storm rolling in over it, then the round's own sky
  opening?: 'sun' | 'storm' | 'done'; // the opening on a first arrival; absent when the sky is simply settled
  sails?: 'set' | 'furled';
  skyKey?: number; // a new value remounts the round's sky strip, so a new round starts from the storm
  zoom?: boolean; // the scene zooms to the deck and fades out (Ready for compass)
  idle?: boolean; // the sea and the canoe move at their idle rate while no round is armed
  reducedMotion?: boolean;
}

export default function BreathSceneArt({ x, armed, swellDur, canoeRef, backBandRef, frontBandRef, drift = 0, previousX = 1, intro = 'round', opening, sails = 'set', skyKey, zoom = false, idle = false, reducedMotion = false }: BreathSceneArtProps) {
  const { stageScale, raisePx } = useBreathStageScale();
  const amp = breathAmp(armed ? swellDur : 3);
  const playState = armed || idle ? 'running' : 'paused';
  // The sun and storm layers are the settled sky and its opening; a new storm rolling in, and the round, draw the strip themselves.
  const skyLayers = intro === 'rest';
  const sunOpacity = opening === 'done' || opening === undefined ? 0 : 1;
  const stormOpacity = opening === 'sun' ? 0 : 1;
  const stormTransform = opening === 'sun' ? 'translateX(220px)' : 'none';
  // The settled sky never animates by itself: it only changes with the opening.
  const layersStill = reducedMotion || opening === undefined;
  const layerFade = 'opacity 2.4s ease';
  const sunTransition = layersStill ? 'none' : layerFade;
  const stormTransition = layersStill ? 'none' : `${layerFade}, transform 2.4s cubic-bezier(.2,.6,.3,1)`;
  // The swell always runs on a 3 s duration; the ramp slows it through playbackRate.
  const swellStyle = { animationDuration: `${SWELL_SEC}s`, animationDelay: '0s', animationPlayState: playState } as const;
  const motion = (frames: Keyframes) => motionFor(frames, reducedMotion);
  // One viewport-wide window onto a strip: it clips the strip's other panel. It sits at the stage's centre; inside the storm layer it
  // fills the layer's height, and out in the stage it is placed to cover the same sky.
  const skyWindow = (stripX: number, options: { drift?: number; inLayer?: boolean; left?: string; rollIn?: boolean; stripKey?: number | string } = {}) => (
    <Box
      key={options.stripKey}
      data-testid="breath-sky-window"
      sx={{
        position: 'absolute', left: options.left ?? `calc(50% - ${SKY_PANEL_W} / 2)`, width: SKY_PANEL_W, overflow: 'hidden', pointerEvents: 'none',
        ...(options.inLayer ? { top: 0, bottom: 0 } : { top: `-${SKY_OVERSHOOT + SKY_ABOVE_STAGE}px`, height: `${SKY_WINDOW_H}px`, zIndex: 1 }),
      }}
    >
      <SkyStrip x={stripX} drift={options.drift} rollIn={options.rollIn} reducedMotion={reducedMotion} />
    </Box>
  );

  return (
    <Box
      aria-hidden
      data-motion={reducedMotion ? 'still' : 'moving'}
      sx={{
        position: 'absolute', left: 0, right: 0, top: `-${ABOVE_HOST_PX}px`, bottom: `-${RUNNER_PLATE_CLEARANCE_PX}px`,
        overflow: 'hidden', zIndex: -1, background: CROSSING_BREATH_STAGE_LOWER,
      }}
    >
      {/* Ready for compass zooms the whole scene about the canoe, then fades it. The origin is the stage's centre,
          written as the same calc as the stage's top, because the root runs from above the host and its own 50% is far off. */}
      <Box
        data-testid="breath-zoom"
        style={{
          transformOrigin: `50% calc(${ABOVE_HOST_PX}px + (100% - ${ABOVE_HOST_PX + RUNNER_PLATE_CLEARANCE_PX}px) / 2 - ${raisePx}px)`,
          transform: zoom ? `scale(${ZOOM_SCALE})` : 'none',
          opacity: zoom ? 0 : 1,
          transition: zoom ? ZOOM_TRANSITION : 'none',
        }}
        sx={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0, pointerEvents: 'none' }}
      >
        <Box
          data-testid="breath-stage"
          style={{ transform: `scale(${round(stageScale, 3)})`, '--breath-stage-scale': round(stageScale, 3) } as CSSProperties}
          sx={{
            position: 'absolute', width: `${STAGE_W}px`, height: `${STAGE_H}px`, transformOrigin: '50% 50%',
            left: `calc(50% - ${STAGE_W / 2}px)`,
            top: `calc(${ABOVE_HOST_PX}px + (100% - ${ABOVE_HOST_PX + RUNNER_PLATE_CLEARANCE_PX}px) / 2 - ${STAGE_H / 2}px - ${raisePx}px)`,
          }}
        >
          <Box
            data-testid="breath-stage-backdrop"
            sx={{
              position: 'absolute', left: `-${REACH}px`, right: `-${REACH}px`, top: `-${REACH}px`, bottom: `-${REACH}px`,
              background: `linear-gradient(${CROSSING_BREATH_STAGE_UPPER} 0 50%, ${CROSSING_BREATH_STAGE_LOWER} 50% 100%)`,
            }}
          />
          <Box
            data-testid="breath-sky-clear"
            sx={{
              position: 'absolute', left: `-${REACH}px`, right: `-${REACH}px`, top: `-${SKY_OVERSHOOT + REACH}px`, height: `${SKY_H + REACH}px`, zIndex: 0,
              background: `linear-gradient(${CROSSING_BREATH_SKY_TOP} ${REACH}px, ${CROSSING_BREATH_SKY_MID} ${REACH + SKY_H * 0.52}px, ${CROSSING_BREATH_SKY_BOTTOM} ${REACH + SKY_H}px)`,
            }}
          />
          {skyLayers && (
            <>
              <Box
                data-testid="breath-sun-layer"
                style={{ opacity: sunOpacity, transition: sunTransition }}
                sx={{
                  position: 'absolute', left: `-${REACH}px`, right: `-${REACH}px`, top: `-${SKY_OVERSHOOT + SKY_ABOVE_STAGE}px`, height: `${SKY_H + SKY_ABOVE_STAGE}px`,
                  zIndex: 5, overflow: 'hidden', pointerEvents: 'none',
                  background: `linear-gradient(${CROSSING_SUN_SCENE_GRADIENT_TOP} ${SKY_ABOVE_STAGE}px, ${CROSSING_SUN_SCENE_GRADIENT_MID} ${SKY_ABOVE_STAGE + SKY_H * 0.55}px, ${CROSSING_SUN_SCENE_GRADIENT_BOTTOM} ${SKY_ABOVE_STAGE + SKY_H}px)`,
                }}
              >
                <Box sx={{ position: 'absolute', left: `calc(50% - ${STAGE_W / 2}px)`, bottom: 0, width: `${STAGE_W}px`, height: `${SKY_H}px` }}>
                  <Box
                    data-testid="breath-sun-layer-sun"
                    sx={{
                      position: 'absolute', left: '12%', top: '34%', width: '48px', height: '48px', borderRadius: '50%', background: CROSSING_SUN_SCENE_SUN,
                      boxShadow: `0 0 0 12px ${CROSSING_SUN_SCENE_SUN_HALO}, 0 0 36px 18px ${CROSSING_SUN_SCENE_SUN_GLOW}`,
                    }}
                  />
                  {SUN_LAYER_CLOUDS.map(cloud => (
                    <Box
                      key={cloud.left}
                      data-testid="breath-sun-layer-cloud"
                      sx={{ position: 'absolute', left: cloud.left, top: cloud.top, width: `${cloud.width}px`, height: `${cloud.height}px` }}
                    >
                      {CLOUD_SHAPES.map(shape => (
                        <Box key={shape.left} sx={{ position: 'absolute', ...shape, background: cloud.fill }} />
                      ))}
                    </Box>
                  ))}
                </Box>
              </Box>
              <Box
                data-testid="breath-storm-layer"
                style={{ opacity: stormOpacity, transform: stormTransform, transition: stormTransition }}
                sx={{
                  position: 'absolute', left: `-${REACH}px`, right: `-${REACH}px`, top: `-${SKY_OVERSHOOT + SKY_ABOVE_STAGE}px`, height: `${SKY_H + SKY_ABOVE_STAGE}px`,
                  zIndex: 5, overflow: 'hidden', pointerEvents: 'none', background: STORM_SKY_GRADIENT,
                }}
              >
                {skyWindow(x, { inLayer: true })}
              </Box>
            </>
          )}

          {intro === 'rolling' && (
            <Box
              data-testid="breath-roll"
              sx={{
                position: 'absolute', left: `calc(50% - ${SKY_PANEL_W} / 2)`, width: `calc(2 * ${SKY_PANEL_W})`, top: `-${SKY_OVERSHOOT + SKY_ABOVE_STAGE}px`, height: `${SKY_WINDOW_H}px`, zIndex: 1, pointerEvents: 'none',
                animationName: motion(pvRoll), animationDuration: `${ROLL_IN_SECONDS}s`, animationTimingFunction: 'cubic-bezier(.25,.6,.3,1)', animationFillMode: 'forwards',
              }}
            >
              {skyWindow(previousX, { inLayer: true, left: '0', stripKey: 'previous' })}
              {skyWindow(0, { inLayer: true, left: SKY_PANEL_W, rollIn: true, stripKey: 'next' })}
            </Box>
          )}
          {intro === 'round' && skyWindow(x, { drift, stripKey: skyKey })}

          <Box data-testid="breath-scene-horizon" sx={{ position: 'absolute', left: `-${REACH}px`, right: `-${REACH}px`, top: `calc(${HORIZON_AT} + 23px)`, bottom: `-${REACH}px`, backgroundColor: CROSSING_SEA_MID }} />
          <Box
            ref={backBandRef}
            data-testid="breath-scene-wave-back"
            style={{ ...swellStyle, transform: `scaleY(${round(amp, 3)})` }}
            sx={{
              position: 'absolute', left: '50%', width: `${WAVE_BAND_W}px`, marginLeft: `-${WAVE_BAND_W / 2}px`, top: `calc(${HORIZON_AT} - 25px)`, height: '60px',
              transformOrigin: '50% 100%', transition: reducedMotion ? 'none' : 'transform 0.6s linear',
              backgroundImage: WAVE_BACK_URL, backgroundSize: '300px 60px', backgroundRepeat: 'repeat-x', backgroundPosition: '0 bottom',
              animationName: motion(rollBack), animationTimingFunction: 'linear', animationIterationCount: 'infinite',
            }}
          />
          <Box data-testid="breath-scene-deep" sx={{ position: 'absolute', left: `-${REACH}px`, right: `-${REACH}px`, top: `calc(${DEEP_AT} + 19px)`, bottom: `-${REACH}px`, backgroundColor: CROSSING_SEA_DEEP }} />
          <Box
            ref={frontBandRef}
            data-testid="breath-scene-wave-front"
            style={{
              animationDuration: `${FRONT_BAND_LOOP_SEC}s`, animationDelay: `${FRONT_BAND_DELAY_SEC}s`, animationPlayState: playState,
              transform: `scaleY(${round(amp, 3)})`,
            }}
            sx={{
              position: 'absolute', left: '50%', width: `${WAVE_BAND_W}px`, marginLeft: `-${WAVE_BAND_W / 2}px`, top: `calc(${DEEP_AT} - 25px)`, height: '56px',
              transformOrigin: '50% 100%', transition: reducedMotion ? 'none' : 'transform 0.6s linear',
              backgroundImage: WAVE_FRONT_URL, backgroundSize: '200px 56px', backgroundRepeat: 'repeat-x', backgroundPosition: '0 bottom',
              animationName: motion(rollFront), animationTimingFunction: 'linear', animationIterationCount: 'infinite',
            }}
          />

          <Box
            data-testid="breath-canoe"
            style={{ top: `calc(${HORIZON_AT} - 14px + ${round(canoeRideHeight(amp), 1)}px)`, transform: `scaleY(${round(amp, 3)})` }}
            sx={{
              position: 'absolute', left: '50%', width: '48px', height: '48px', margin: '-24px 0 0 -24px', zIndex: 6, pointerEvents: 'none',
              transformOrigin: '50% 50%', transition: reducedMotion ? 'none' : 'transform 0.6s linear, top 0.6s linear',
            }}
          >
            <Box
              ref={canoeRef}
              data-testid="breath-canoe-swell"
              style={swellStyle}
              sx={{ display: 'block', width: '48px', height: '48px', animationName: motion(swellCanoe), animationTimingFunction: 'ease-in-out', animationIterationCount: 'infinite' }}
            >
              <Box
                data-testid="breath-canoe-sprite"
                style={{ transform: `scaleY(${round(1 / amp, 3)})` }}
                sx={{ position: 'relative', width: '48px', height: '48px', transformOrigin: '50% 50%' }}
              >
                <WaaCanoe sails={sails} reducedMotion={reducedMotion} />
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
